import { Label, MultiSelectPills, Textarea } from '@artstorefronts/ui';
import { gql, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

interface VoiceOption {
  id: string;
  name: string;
}

interface MyVoiceFormProps {
  selectedFormality: VoiceOption | null;
  onChangeFormality: (selectedOption: VoiceOption | null) => void;
  selectedTones: any[];
  onChangeTones: (selectedOptions: VoiceOption[]) => void;
  avoidWords: string;
  onChangeAvoidWords: (value: string) => void;
}

const QUERY = gql`
  query myVoiceForm {
    voiceFormalities {
      id
      name
    }
    voiceTones {
      id
      name
    }
  }
`;

const MyVoiceForm = ({
  selectedFormality,
  onChangeFormality,
  selectedTones,
  onChangeTones,
  avoidWords,
  onChangeAvoidWords,
}: MyVoiceFormProps) => {
  const { t } = useTranslation('chat');
  const { data, loading } = useQuery<undefined | { voiceFormalities: VoiceOption[]; voiceTones: VoiceOption[] }>(QUERY);

  if (loading || !data) return null;

  const formalityOptions = data.voiceFormalities;
  const selectedFormalityOptions = selectedFormality ? [selectedFormality?.name] : [];
  const selectedFormalityOptionsNames = formalityOptions.map((f: VoiceOption) => f.name);

  const toneOptions = data.voiceTones;
  const toneOptionsNames = toneOptions.map((t) => t.name);
  const selectedTonesOptions = selectedTones.map((t: VoiceOption) => t.name);

  const handleFormalityChange = (options: string[]) => {
    const formality = formalityOptions.find((f: VoiceOption) => f.name === options[0]);
    onChangeFormality(formality || null);
  };

  const handleToneChange = (options: string[]) => {
    const tones = toneOptions?.filter((i) => options.includes(i.name));

    onChangeTones(tones!);
  };

  const handleChangeAvoidWords = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChangeAvoidWords(e.target.value);
  };

  return (
    <div className="grid gap-4 pt-4">
      <div>
        <Label htmlFor="formality" className="mb-2 block">
          {t('formality')}
          <small className="text-xs text-gray-500 ml-1">{t('choose-one')}</small>
        </Label>
        <MultiSelectPills
          options={selectedFormalityOptionsNames}
          selectedOptions={selectedFormalityOptions}
          maxSelected={1}
          onChange={handleFormalityChange}
        />
      </div>
      <div>
        <Label htmlFor="tone" className="my-2 block">
          {t('describe-your-tone')}
          <small className="text-xs text-gray-500 ml-1">{t('choose-up-to-four')}</small>
        </Label>
        <MultiSelectPills
          options={toneOptionsNames}
          selectedOptions={selectedTonesOptions}
          maxSelected={4}
          onChange={handleToneChange}
        />
      </div>
      <div>
        <Label htmlFor="avoid-words" className="my-2 block">
          {t('avoid-these-words')}
        </Label>
        <Textarea
          className="col-span-3 rounded-md"
          placeholder={t('avoid-words-placeholder')}
          id="avoid-words"
          value={avoidWords}
          onChange={handleChangeAvoidWords}
        />
      </div>
    </div>
  );
};

export default MyVoiceForm;
